import Reveal, { SectionLabel } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/site";

export default function About() {
  return (
    <>
      <PageHero
        label="About The Academy"
        title={<>Where voices are <em className="font-italic-accent italic gold-text">discovered</em>, and futures are shaped.</>}
        subtitle="Beyond The Stage Academy is a premium enrichment institute — a sanctuary of craft, character and confident expression."
      />

      {/* Story */}
      <section data-testid="about-story" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><SectionLabel>Our Story</SectionLabel></Reveal>
          </div>
          <div className="lg:col-span-8 space-y-7 font-sans text-base lg:text-lg text-white/70 leading-relaxed font-light">
            <Reveal delay={0.1}><p>Beyond The Stage Academy was founded on the belief that communication is one of life's most valuable skills. Through speech, drama, public speaking, leadership training and confidence-building experiences, the academy helps learners develop not only performance skills but also the courage to express themselves authentically in every aspect of life.</p></Reveal>
            <Reveal delay={0.2}><p>The stage is only the beginning. The goal is confidence beyond the stage — a voice that carries into classrooms, boardrooms and every important room a learner will ever enter.</p></Reveal>
            <Reveal delay={0.3}><p>We built BTSA as a deliberate alternative to the noisy, cookie-cutter enrichment world — an academy where small cohorts, serious craft and personal mentorship matter more than crowded certificates.</p></Reveal>
          </div>
        </div>
      </section>

      {/* Vision + Mission + Philosophy */}
      <section data-testid="about-vm" className="py-24 lg:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-6 lg:gap-8">
          <Reveal>
            <div data-testid="about-vision" className="lux-card p-10 h-full">
              <SectionLabel>Vision</SectionLabel>
              <h3 className="mt-5 font-serif text-2xl text-white font-medium uppercase tracking-wide leading-tight">A generation that can <span className="gold-text">speak, think and lead</span></h3>
              <p className="mt-5 font-sans text-white/60 leading-relaxed font-light">A world where every individual knows their voice matters — and possesses the craft to use it meaningfully.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="about-mission" className="lux-card p-10 h-full">
              <SectionLabel>Mission</SectionLabel>
              <h3 className="mt-5 font-serif text-2xl text-white font-medium uppercase tracking-wide leading-tight">To cultivate <span className="gold-text">confidence and character</span></h3>
              <p className="mt-5 font-sans text-white/60 leading-relaxed font-light">Through speech, drama, public speaking, leadership and creative thinking — building skills that last well beyond the classroom.</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div data-testid="about-philosophy" className="lux-card p-10 h-full">
              <SectionLabel>Philosophy</SectionLabel>
              <h3 className="mt-5 font-serif text-2xl text-white font-medium uppercase tracking-wide leading-tight">Rehearse them <span className="gold-text">into confidence</span></h3>
              <p className="mt-5 font-sans text-white/60 leading-relaxed font-light">We don't lecture children into confidence. We rehearse them into it — through performance, reflection, refinement and applause.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Founder */}
      <section data-testid="about-founder" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative hairline aspect-[4/5] overflow-hidden">
                <img src={BRAND.founder_image} alt={BRAND.founder_name} className="absolute inset-0 w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <SectionLabel>Meet The Founder</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-white font-medium uppercase tracking-wide leading-tight">{BRAND.founder_name}</h2>
              <div className="mt-3 font-italic-accent italic text-xl text-[#d4af37]">{BRAND.founder_role}</div>
              <div className="mt-8 space-y-5 font-sans text-base lg:text-lg text-white/65 leading-relaxed font-light">
                <p>Kiran Pandit founded Beyond The Stage Academy with a singular conviction — that every child and teen deserves to be heard, and possesses the right to be taught how. With years of experience in communication coaching, drama and leadership facilitation, Kiran has mentored hundreds of young learners to discover the quiet certainty of their own voice.</p>
                <p>Under her guidance, BTSA has grown into a recognised destination for serious, personalised and transformative communication training.</p>
              </div>
              <Link to="/contact" className="mt-9 inline-flex font-sans text-[11px] tracking-[0.3em] uppercase text-[#d4af37] items-center gap-3">Speak With Kiran →</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="about-values" className="py-24 lg:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal>
              <div className="flex justify-center"><SectionLabel>Our Core Values</SectionLabel></div>
              <h2 className="mt-7 font-serif text-4xl lg:text-5xl text-white font-medium uppercase tracking-wide leading-tight">The pillars of <span className="gold-text">BTSA</span></h2>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {[
              {l:"C",t:"Communication",d:"Speaking and listening with clarity, care and craft."},
              {l:"C",t:"Confidence",d:"The quiet certainty of knowing one's voice matters."},
              {l:"C",t:"Creativity",d:"Original thought, imaginative expression, the courage to make."},
              {l:"L",t:"Leadership",d:"Decisiveness, empathy and the courage to lead."},
              {l:"E",t:"Excellence",d:"Every interaction reflects quality and professionalism."},
            ].map((v) => (
              <Reveal key={v.t}>
                <div data-testid={`value-${v.t.toLowerCase()}`} className="bg-[#0a0a0a] p-8 h-full text-center">
                  <div className="font-serif text-7xl gold-text leading-none">{v.l}</div>
                  <h3 className="mt-5 font-serif text-lg text-white font-medium uppercase tracking-wide">{v.t}</h3>
                  <p className="mt-3 font-sans text-sm text-white/55 leading-relaxed font-light">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
